// FocusView Component - Focus mode with card flipping UI
import React, { useState, useEffect } from 'react';
import { ABLE_COLORS, ENCOURAGING_MESSAGES } from '../constants';

export default function FocusView({
  filteredTasks,
  currentTask,
  focusIndex,
  isCardFlipped,
  setIsCardFlipped,
  onSkip,
  onMarkDone
}) {
  const [message, setMessage] = useState('');
  
  useEffect(() => {
    // Pick a random encouraging message on component mount
    const randomMessage = ENCOURAGING_MESSAGES[Math.floor(Math.random() * ENCOURAGING_MESSAGES.length)];
    setMessage(randomMessage);
  }, []);
  
  return (
    <>
      {/* Focus Card Container */}
      <div style={{
        background: ABLE_COLORS.neutral,
        borderRadius: ABLE_COLORS.cardRadius,
        padding: '40px 32px',
        boxShadow: ABLE_COLORS.cardShadowElevated,
        minHeight: '400px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        marginBottom: '20px',
        border: `2px solid ${ABLE_COLORS.borderLight}`
      }}>
        {filteredTasks.length === 0 ? (
          // Empty state
          <div style={{ textAlign: 'center' }}>
            <h2 style={{ 
              fontSize: '24px', 
              color: ABLE_COLORS.primary, 
              margin: '0 0 12px 0',
              fontWeight: '600'
            }}>
              Nothing to focus on right now
            </h2>
            <p style={{
              fontSize: '16px',
              color: ABLE_COLORS.textMuted,
              margin: 0
            }}>
              Add some tasks or adjust your filters
            </p>
          </div>
        ) : currentTask ? (
          // Card with flip animation
          <div 
            onClick={() => setIsCardFlipped(!isCardFlipped)}
            style={{
              minHeight: '200px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              textAlign: 'center',
              cursor: 'pointer'
            }}
          >
            {!isCardFlipped ? (
              // Front of card - task title
              <>
                <h2 style={{ 
                  margin: '0 0 24px 0',
                  fontSize: '32px',
                  color: ABLE_COLORS.primary,
                  fontWeight: '600',
                  lineHeight: '1.4'
                }}>
                  {currentTask.title}
                </h2>
                <p style={{
                  fontSize: '14px',
                  color: ABLE_COLORS.textMuted,
                  margin: 0
                }}>
                  {focusIndex + 1} of {filteredTasks.length}
                </p>
                {currentTask.notes && (
                  <p style={{ 
                    fontSize: '13px',
                    color: ABLE_COLORS.accent,
                    margin: '16px 0 0 0',
                    fontStyle: 'italic'
                  }}>
                    Tap to see notes
                  </p>
                )}
              </>
            ) : (
              // Back of card - notes
              <>
                <p style={{
                  fontSize: '18px',
                  color: ABLE_COLORS.textSecondary,
                  margin: 0,
                  lineHeight: '1.6',
                  whiteSpace: 'pre-wrap'
                }}>
                  {currentTask.notes || 'No notes yet'}
                </p>
                <p style={{ 
                  fontSize: '13px',
                  color: ABLE_COLORS.accent,
                  margin: '24px 0 0 0',
                  fontStyle: 'italic'
                }}>
                  Tap to flip back
                </p>
              </>
            )}
          </div>
        ) : null}
      </div>

      {/* Action Buttons - only show when there's a current task */}
      {currentTask && (
        <>
          <div style={{
            background: ABLE_COLORS.neutral,
            borderRadius: ABLE_COLORS.cardRadius,
            padding: '24px',
            boxShadow: ABLE_COLORS.cardShadow,
            marginBottom: '20px',
            border: `2px solid ${ABLE_COLORS.borderLight}`
          }}>
            <div style={{ display: 'flex', gap: '16px' }}>
              <button
                onClick={onSkip}
                style={{
                  flex: 1,
                  padding: '16px',
                  background: ABLE_COLORS.priority.someday,
                  color: 'white',
                  border: 'none',
                  borderRadius: ABLE_COLORS.buttonRadius,
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '600',
                  boxShadow: `0 4px 12px ${ABLE_COLORS.priority.someday}30`,
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = `0 6px 20px ${ABLE_COLORS.priority.someday}40`;
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = `0 4px 12px ${ABLE_COLORS.priority.someday}30`;
                }}
              >
                Not Now
              </button>
              <button
                onClick={() => {
                  console.log('Done button clicked for task:', currentTask.id, currentTask.title);
                  onMarkDone(currentTask.id);
                }}
                style={{
                  flex: 1,
                  padding: '16px',
                  background: `linear-gradient(135deg, ${ABLE_COLORS.semantic.success} 0%, ${ABLE_COLORS.state.physical} 100%)`,
                  color: 'white',
                  border: 'none',
                  borderRadius: ABLE_COLORS.buttonRadius,
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '600',
                  boxShadow: `0 4px 12px ${ABLE_COLORS.semantic.success}30`,
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = `0 6px 20px ${ABLE_COLORS.semantic.success}40`;
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = `0 4px 12px ${ABLE_COLORS.semantic.success}30`;
                }}
              >
                Done!
              </button>
            </div>
          </div>

          {/* Encouraging message */}
          <div style={{
            background: ABLE_COLORS.bg,
            borderRadius: ABLE_COLORS.cardRadius,
            padding: '16px',
            textAlign: 'center'
          }}>
            <p style={{
              margin: 0,
              color: ABLE_COLORS.textMuted,
              fontSize: '13px',
              fontWeight: '400',
              fontStyle: 'italic'
            }}>
              {message}
            </p>
          </div>
        </>
      )}
    </>
  );
}
